import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, CircularProgress, TextField } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import EditIcon from "@material-ui/icons/Edit";

import { generateDraft, updateDraftBody, decideDraft, sendDraft } from "../../Redux/Actions/agent";
import { Avatar, Draft } from "../../Redux/Sagas/agent/api";
import VideoPanel from "./VideoPanel";
import { draftChip, formatDate } from "./status";

interface IProps {
  message: any;
  avatars: Avatar[];
  videoEnabled: boolean;
  draftPending: Record<string, string>;
  // True when the inbox stopped polling for background drafts (time cap hit).
  draftPollExpired?: boolean;
}

const PENDING_LABEL: Record<string, string> = {
  save: "Saving...",
  approve: "Approving...",
  reject: "Rejecting...",
  send: "Sending...",
};

const DraftPanel: React.FC<IProps> = ({
  message,
  avatars,
  videoEnabled,
  draftPending,
  draftPollExpired,
}) => {
  const dispatch = useDispatch();
  const draft: Draft | null = message.draft;
  const generating = draftPending[message.id] === "generate";
  const pendingAction = draft ? draftPending[draft.id] : undefined;
  const busy = Boolean(pendingAction) || generating;

  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  const savedFrom = useRef<string | null>(null);

  const text = draft ? draft.editedBody || draft.body || "" : "";

  // Leave edit mode once a save/approve request finished and the draft changed.
  useEffect(() => {
    if (savedFrom.current !== null && !pendingAction) {
      if (draft && draft.updatedAt !== savedFrom.current) setEditing(false);
      savedFrom.current = null;
    }
  }, [pendingAction, draft]);

  // Switching to another message cancels editing.
  useEffect(() => {
    setEditing(false);
  }, [message.id]);

  const submitEdit = (approve: boolean) => {
    if (!draft) return;
    savedFrom.current = draft.updatedAt;
    if (approve) dispatch(decideDraft(draft.id, "approve", editBody));
    else dispatch(updateDraftBody(draft.id, editBody));
  };

  const regenerate = (force: boolean) => (
    <Button
      variant={force ? "outlined" : "contained"}
      className={force ? undefined : "aiPrimaryButton"}
      disabled={generating}
      onClick={() => dispatch(generateDraft(message.id, force || undefined))}
    >
      {generating ? "Writing a reply..." : force ? "Try again" : "Generate draft"}
    </Button>
  );

  if (message.draftPending) {
    return (
      <div className="aiEmpty">
        <CircularProgress size={28} />
        <div>Writing a reply...</div>
        <div className="aiMuted">
          {draftPollExpired
            ? "This is taking longer than usual. Use Refresh to check again."
            : "This usually takes under a minute. The draft appears here on its own."}
        </div>
        <Button variant="contained" className="aiPrimaryButton" disabled>
          Writing a reply...
        </Button>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="aiEmpty">
        {message.draftError ? (
          <div className="aiNotice aiNotice--error">
            The agent could not write a draft: {message.draftError}
          </div>
        ) : (
          <div>No draft yet for this message.</div>
        )}
        {regenerate(Boolean(message.draftError))}
      </div>
    );
  }

  const chip = draftChip(message);
  const canEdit = ["pending", "approved"].includes(draft.status);

  const actions = () => {
    if (editing) {
      return (
        <div className="aiActions">
          <Button
            variant="contained"
            className="aiPrimaryButton"
            startIcon={<CheckIcon />}
            disabled={busy || !editBody.trim()}
            onClick={() => submitEdit(true)}
          >
            {pendingAction === "approve" ? "Approving..." : "Save & approve"}
          </Button>
          <Button variant="outlined" disabled={busy || !editBody.trim()} onClick={() => submitEdit(false)}>
            {pendingAction === "save" ? "Saving..." : "Save"}
          </Button>
          <Button disabled={busy} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      );
    }
    switch (draft.status) {
      case "pending":
        return (
          <div className="aiActions">
            <Button
              variant="contained"
              className="aiPrimaryButton"
              startIcon={<CheckIcon />}
              disabled={busy}
              onClick={() => dispatch(decideDraft(draft.id, "approve"))}
            >
              {pendingAction === "approve" ? "Approving..." : "Approve"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={busy}
              onClick={() => dispatch(decideDraft(draft.id, "reject"))}
            >
              {pendingAction === "reject" ? "Rejecting..." : "Reject"}
            </Button>
          </div>
        );
      case "approved":
        return (
          <div className="aiActions">
            <Button
              variant="contained"
              className="aiPrimaryButton"
              startIcon={<SendIcon />}
              disabled={busy}
              onClick={() => dispatch(sendDraft(draft.id))}
            >
              {pendingAction === "send"
                ? "Sending..."
                : draft.video && draft.video.status === "ready" && !draft.video.stale
                ? "Send reply with video"
                : "Send reply"}
            </Button>
          </div>
        );
      case "sending":
        return <div className="aiMuted">Sending...</div>;
      case "sent":
        return (
          <div className="aiMuted">
            {draft.autoSent ? "Sent automatically" : "Sent"}
            {draft.sentAt ? ` on ${formatDate(draft.sentAt)}` : ""}
          </div>
        );
      case "failed":
        return (
          <>
            <div className="aiNotice aiNotice--error">
              The reply was not sent: {draft.error || "unknown error"}
            </div>
            <div className="aiActions">
              <Button
                variant="contained"
                className="aiPrimaryButton"
                startIcon={<SendIcon />}
                disabled={busy}
                onClick={() => dispatch(sendDraft(draft.id))}
              >
                {pendingAction === "send" ? "Sending..." : "Retry send"}
              </Button>
            </div>
          </>
        );
      case "rejected":
        return <div className="aiMuted">Rejected. Nothing will be sent.</div>;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="aiChips">
        {chip && <span className={`aiChip aiChip--${chip.tone}`}>{chip.label}</span>}
        {draft.classification && <span className="aiMuted">{draft.classification}</span>}
        {typeof draft.confidence === "number" && (
          <span className="aiMuted">confidence {Math.round(draft.confidence * 100)}%</span>
        )}
        {pendingAction && PENDING_LABEL[pendingAction] && (
          <span className="aiMuted">{PENDING_LABEL[pendingAction]}</span>
        )}
      </div>

      {message.draftError && (
        <div className="aiNotice aiNotice--error" style={{ marginTop: 10 }}>
          Regenerating failed: {message.draftError}
        </div>
      )}

      {draft.decision !== "reply" ? (
        <>
          <div className="inboxDraftBody">
            {draft.decision === "escalate"
              ? "The agent passed this one to you."
              : "The agent decided no reply is needed."}
            {draft.reason ? ` Reason: ${draft.reason}` : ""}
          </div>
          {draft.status !== "sent" && regenerate(true)}
        </>
      ) : (
        <>
          {editing ? (
            <TextField
              value={editBody}
              onChange={(event) => setEditBody(event.target.value)}
              variant="outlined"
              multiline
              minRows={8}
              fullWidth
              className="aiField"
              style={{ marginTop: 12 }}
              disabled={busy}
            />
          ) : (
            <div className="inboxDraftBody">{text}</div>
          )}
          {!editing && canEdit && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              disabled={busy}
              onClick={() => {
                setEditBody(text);
                setEditing(true);
              }}
              style={{ marginBottom: 10 }}
            >
              Edit
            </Button>
          )}
          {actions()}
          {draft.reason && <div className="aiMuted" style={{ marginTop: 10 }}>Agent note: {draft.reason}</div>}
          {videoEnabled && draft.status !== "rejected" && (
            <VideoPanel draft={draft} avatars={avatars} pendingAction={pendingAction} />
          )}
        </>
      )}

      {draft.events && draft.events.length > 0 && (
        <div className="inboxSection">
          <div className="inboxSectionTitle">Activity</div>
          <ol className="inboxTimeline">
            {draft.events.map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <time dateTime={event.at}>{formatDate(event.at)}</time>
                {event.message}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default DraftPanel;
